import React, { useState } from 'react';
import { MONTHS } from '../calculations/monthlyNeedCalculations.js';
import { parseMeteoFranceFile, computeStationMonthlyAverage } from '../calculations/meteoImport.js';
import { searchCity, fetchClimateMonthlyRainfall } from '../calculations/openMeteoClient.js';
import { effectiveRoofArea, computeCollectedMonthly } from '../calculations/roofCollectionCalculations.js';
import MonthlyBarChart from '../MonthlyBarChart.js';
import ChartExportToolbar from '../ChartExportToolbar.js';

/**
 * Étape 3 : pluviométrie mensuelle — import fichier Météo-France (CSV/CSV.gz), recherche de
 * ville via Open-Meteo, ou saisie manuelle en repli. Toutes ces sources alimentent le même
 * état `rainfall` (mm/mois), qui alimente ensuite le volume collecté (surface utile de l'étape 2).
 */
const RainfallStep = ({ rainfall, setRainfall, roofs, currentStep, totalSteps, goPrevious, goNext, canAdvance }) => {
  const [importStatus, setImportStatus] = useState({ text: '', kind: '' });
  const [stationStatus, setStationStatus] = useState({ text: '', kind: '' });
  const [cityStatus, setCityStatus] = useState({ text: '', kind: '' });
  const [meteoStations, setMeteoStations] = useState(null);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [yearRangeN, setYearRangeN] = useState(10);
  const [cityQuery, setCityQuery] = useState('');
  const [cityResults, setCityResults] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const effectiveArea = effectiveRoofArea(roofs);
  const collected = computeCollectedMonthly(roofs, rainfall);
  const collectedTotal = collected.reduce((a, b) => a + b, 0);
  const rainTotal = rainfall.reduce((a, b) => a + b, 0);

  const handleFile = async (file) => {
    setMeteoStations(null);
    setSelectedStationId(null);
    setImportStatus({ text: 'Lecture du fichier…', kind: 'busy' });
    try {
      const { stations, lineCount } = await parseMeteoFranceFile(file, (text) => setImportStatus({ text, kind: 'busy' }));
      setMeteoStations(stations);
      const firstId = Object.keys(stations).sort((a, b) => stations[a].name.localeCompare(stations[b].name))[0];
      setSelectedStationId(firstId);
      const years = Object.keys(stations[firstId].monthly).map(Number).sort((a, b) => a - b);
      setStationStatus({
        text: `Données disponibles de ${years[0]} à ${years[years.length - 1]} pour cette station (${years.length} année(s) avec des relevés).`,
        kind: '',
      });
      setImportStatus({ text: `${Object.keys(stations).length} station(s) trouvée(s) — ${lineCount.toLocaleString('fr-FR')} lignes lues.`, kind: 'ok' });
    } catch (err) {
      setImportStatus({ text: err.message, kind: 'bad' });
    }
  };

  const onStationChange = (id) => {
    setSelectedStationId(id);
    const st = meteoStations[id];
    const years = Object.keys(st.monthly).map(Number).sort((a, b) => a - b);
    setStationStatus({
      text: `Données disponibles de ${years[0]} à ${years[years.length - 1]} pour cette station (altitude ${Math.round(st.alt || 0) || '?'} m, ${years.length} année(s) avec des relevés).`,
      kind: '',
    });
  };

  const computeAverage = () => {
    const st = meteoStations[selectedStationId];
    const years = Object.keys(st.monthly).map(Number);
    const minY = Math.min(...years), maxY = Math.max(...years);
    const yearEnd = maxY, yearStart = Math.max(minY, maxY - yearRangeN + 1);
    const { values, usedFallback } = computeStationMonthlyAverage(st, yearStart, yearEnd);
    setRainfall(values.map(v => Math.round(v * 10) / 10));
    let msg = `Moyennes mensuelles calculées pour ${st.name} (${yearStart}–${yearEnd}).`;
    if (usedFallback) msg += ' Certains mois avaient peu de relevés sur cette plage : la moyenne inclut aussi les années partiellement renseignées pour ces mois.';
    setStationStatus({ text: msg, kind: 'ok' });
  };

  const runCitySearch = async () => {
    const q = cityQuery.trim();
    setCityResults([]);
    if (!q) { setCityStatus({ text: 'Entrez un nom de ville.', kind: 'bad' }); return; }
    setCityStatus({ text: 'Recherche…', kind: 'busy' });
    try {
      const results = await searchCity(q);
      if (results.length === 0) {
        setCityStatus({ text: 'Aucune ville trouvée. Essayez un autre nom, ou renseignez la pluviométrie manuellement.', kind: 'bad' });
        return;
      }
      setCityStatus({ text: `${results.length} résultat(s) — choisissez une ville :`, kind: 'ok' });
      setCityResults(results);
    } catch (err) {
      setCityStatus({ text: "Recherche indisponible dans cet environnement (réseau restreint). Renseignez la pluviométrie manuellement ci-dessous.", kind: 'bad' });
    }
  };

  const pickCity = async (r) => {
    setCityStatus({ text: `Récupération des normales pour ${r.name}…`, kind: 'busy' });
    try {
      const values = await fetchClimateMonthlyRainfall(r.latitude, r.longitude);
      setRainfall(values);
      setCityResults([]);
      setCityStatus({ text: `Normales 2015–2024 récupérées pour ${r.name}.`, kind: 'ok' });
    } catch (err) {
      setCityStatus({ text: 'Récupération impossible dans cet environnement (réseau restreint). Renseignez la pluviométrie manuellement ci-dessous.', kind: 'bad' });
    }
  };

  const setRainMonth = (m, value) => setRainfall(prev => prev.map((v, i) => i === m ? value : v));

  const getChartDataTSV = () => {
    const rows = ['Mois\tCollecté (m³)'];
    MONTHS.forEach((m, i) => rows.push(`${m}\t${collected[i].toFixed(3)}`));
    return rows.join('\n');
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Pluviométrie et volume collecté</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">

        <div className="rw-sub-label">Importer un fichier Météo-France (recommandé)</div>
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">
            Fonctionne entièrement dans le navigateur, sans connexion réseau au moment du calcul.
            Télécharge un fichier « données climatologiques quotidiennes » par département sur{' '}
            <a href="https://www.data.gouv.fr/fr/datasets/donnees-climatologiques-de-base-quotidiennes/" target="_blank" rel="noopener noreferrer">data.gouv.fr</a>{' '}
            (nom du type <code>Q_&lt;département&gt;_previous-1950-2024_RR-T-Vent.csv.gz</code>), puis dépose-le ici — .csv ou .csv.gz.
          </p>
        </div>
        <div
          className={`rw-dropzone ${dragOver ? 'rw-dropzone--drag-over' : ''}`}
          onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
          onDrop={onDrop}
        >
          <input
            type="file" accept=".csv,.gz"
            onChange={e => { const f = e.target.files[0]; if (f) handleFile(f); }}
          />
          <p className="rw-dropzone-hint">📁 Glisse ton fichier ici, ou utilise le bouton ci-dessus</p>
        </div>
        {importStatus.text && <div className={`rw-status rw-status--${importStatus.kind}`}>{importStatus.text}</div>}

        {meteoStations && (
          <div className="rw-import-controls">
            <select className="rw-profile-select" value={selectedStationId || ''} onChange={e => onStationChange(e.target.value)}>
              {Object.entries(meteoStations)
                .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                .map(([id, st]) => <option key={id} value={id}>{st.name} ({id})</option>)}
            </select>
            <div className="rw-city-row">
              <label>Période</label>
              <select className="rw-profile-select" value={yearRangeN} onChange={e => setYearRangeN(+e.target.value)}>
                {[1, 3, 5, 10].map(n => <option key={n} value={n}>{n === 1 ? 'Dernière année' : `${n} dernières années`}</option>)}
              </select>
              <button type="button" className="rw-add-btn" onClick={computeAverage}>Calculer la moyenne mensuelle</button>
            </div>
            {stationStatus.text && <div className={`rw-status rw-status--${stationStatus.kind}`}>{stationStatus.text}</div>}
          </div>
        )}

        <div className="rw-sub-label">Ou rechercher une ville en ligne</div>
        <p className="apv2-sim-hint">Nécessite un accès réseau vers l'API Open-Meteo. Récupère la moyenne des précipitations 2015–2024.</p>
        <div className="rw-city-row">
          <input
            type="text" className="rw-city-input" placeholder="ex. Bordeaux, Lyon, Rennes…"
            value={cityQuery} onChange={e => setCityQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runCitySearch()}
          />
          <button type="button" className="rw-add-btn" onClick={runCitySearch}>Rechercher</button>
        </div>
        {cityResults.length > 0 && (
          <div className="rw-city-results">
            {cityResults.map((r, i) => (
              <button key={i} type="button" className="rw-city-result" onClick={() => pickCity(r)}>
                {r.name}{r.admin1 ? ` — ${r.admin1}` : ''}{r.postcodes ? ` (${r.postcodes[0]})` : ''}
              </button>
            ))}
          </div>
        )}
        {cityStatus.text && <div className={`rw-status rw-status--${cityStatus.kind}`}>{cityStatus.text}</div>}

        <div className="rw-sub-label">Pluviométrie mensuelle</div>
        <p className="apv2-sim-hint">En mm/mois (1 mm de pluie = 1 L par m²). Modifiable manuellement à tout moment.</p>
        <div className="rw-rain-grid">
          {MONTHS.map((m, i) => (
            <div key={i} className="rw-rain-cell">
              <label>{m}</label>
              <input type="number" min="0" step="1" value={rainfall[i]} onChange={e => setRainMonth(i, +e.target.value || 0)} />
            </div>
          ))}
        </div>

        <div className="apv2-summary-cards">
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{rainTotal.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} mm</span>
            <span className="apv2-summary-label">pluviométrie annuelle</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{collectedTotal.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
            <span className="apv2-summary-label">volume collecté annuel</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">{effectiveArea.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m²</span>
            <span className="apv2-summary-label">surface de collecte utilisée</span>
          </div>
        </div>

        <div className="rw-chart-block">
          <p className="rw-chart-title">Volume d'eau de pluie collecté</p>
          <ChartExportToolbar containerId="rw-collect-chart" getDataTSV={getChartDataTSV} />
          <div id="rw-collect-chart">
            <MonthlyBarChart values={collected} color="#0a4d5c" />
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default RainfallStep;
